import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { SeoHead } from "@/components/SeoHead";
import { motion } from "framer-motion";
import { Phone, Shield, Clock, MapPin, Droplets, AlertTriangle, Wrench, Zap, ArrowRight, CheckCircle2, FileText } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { PHONE_NUMBER, PHONE_HREF } from "@/components/EmergencyBanner";
import { geoPages, allGeoTowns, type GeoPageData } from "@/data/geoPages";
import heroBg from "@/assets/hero-bg.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0, 0, 0.2, 1] as const } },
};

const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const commonIssues = [
  { icon: Droplets, title: "Complete Water Loss", desc: "Pump motor failure, electrical issues, or a dropped water table causing zero water flow." },
  { icon: AlertTriangle, title: "Low Water Pressure", desc: "Failing pressure tank, clogged filter, worn impeller, or undersized pump struggling to keep up." },
  { icon: Zap, title: "Pump Short Cycling", desc: "Waterlogged pressure tank, faulty pressure switch, or a leak in the system causing rapid on/off cycling." },
  { icon: Wrench, title: "Grinder Pump Alarm", desc: "Sewage grinder pump failure from clogs, float switch issues, or motor burnout requiring immediate attention." },
];

const servicesOffered = [
  { label: "Well Pump Repair & Replacement", href: "/services/well-pump-repair" },
  { label: "Well Pump Installation", href: "/services/well-pump-installation" },
  { label: "Grinder Pump Repair", href: "/services/grinder-pump-repair" },
  { label: "Sewage Pump Systems", href: "/services/sewage-pump-systems" },
  { label: "Water Filtration", href: "/services/water-filtration" },
  { label: "Emergency Same-Day Service", href: "/services/emergency-pump-service" },
];

const GeoServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const data = geoPages.find(p => p.slug === slug);

  if (!data) {
    return (
      <Layout>
        <SeoHead
          title="Service Area Not Found | Semper Fi Pump Service"
          description="This service area page does not exist. View our full Pocono Mountains coverage area."
          statusCode={404}
        />
        <div className="container py-24 text-center">
          <h1 className="text-3xl font-bold mb-3">Service Area Not Found</h1>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            We couldn't find a service area page for that location. Browse all the Pocono towns we serve.
          </p>
          <Link to="/service-area" className="text-accent underline">View All Service Areas</Link>
        </div>
      </Layout>
    );
  }

  const otherTowns = allGeoTowns.filter(t => t.slug !== slug);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Well Pump Repair ${data.town} ${data.state}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Semper Fi Well & Pump Services",
      "address": { "@type": "PostalAddress", "addressLocality": "Saylorsburg", "addressRegion": "PA" },
      "telephone": "+18664241863",
    },
    "areaServed": { "@type": "City", "name": data.town, "containedInPlace": { "@type": "State", "name": "Pennsylvania" } },
    "description": data.metaDesc,
  };

  return (
    <Layout>
      <SeoHead title={data.metaTitle} description={data.metaDesc} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt={`Well pump service in ${data.town}`} className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/50" />
        </div>
        <div className="relative container">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/20 border border-accent/40 rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-accent" />
              <span className="text-sm font-semibold text-accent">{data.county} · {data.town}, {data.state}</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-primary-foreground mb-6 leading-tight font-display">
              Well Pump Repair in<br />{data.town}, {data.state}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-primary-foreground/70 mb-8 leading-relaxed max-w-xl text-pretty">
              {data.localIntro.slice(0, 200)}...
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
              <Button asChild variant="hero" size="xl">
                <a href={PHONE_HREF}>
                  <Phone className="w-5 h-5" />
                  Call Now — {PHONE_NUMBER}
                </a>
              </Button>
              <Button asChild variant="heroOutline" size="xl">
                <Link to="/contact">
                  <FileText className="w-5 h-5" />
                  Free Quote
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ LOCAL INTRO ═══════════ */}
      <section className="container py-16 md:py-20 max-w-4xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-accent" />
            <span className="text-sm font-bold text-accent uppercase tracking-wider">Veteran-Owned & Operated</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold mb-6 font-display">
            Your Local Well Pump Expert in {data.town}
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed text-pretty mb-8">
            {data.localIntro}
          </motion.p>

          <motion.h3 variants={fadeUp} className="text-xl font-bold mb-4">
            Terrain & Well Conditions in {data.town}
          </motion.h3>
          <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed text-pretty mb-8">
            {data.terrainNotes}
          </motion.p>

          <motion.h3 variants={fadeUp} className="text-xl font-bold mb-4">
            Local Water Quality
          </motion.h3>
          <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed text-pretty mb-8">
            {data.waterConditions}
          </motion.p>

          <motion.h3 variants={fadeUp} className="text-xl font-bold mb-4">
            Areas We Service Near {data.town}
          </motion.h3>
          <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed text-pretty">
            {data.neighborhoodMentions}
          </motion.p>
        </motion.div>
      </section>

      {/* ═══════════ COMMON ISSUES ═══════════ */}
      <section className="bg-secondary/50 py-16 md:py-20">
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold mb-10 font-display text-center">
              Common Water System Issues in {data.town}
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {commonIssues.map(issue => (
                <motion.div key={issue.title} variants={fadeUp} className="bg-background rounded-xl p-6 shadow-card border border-border">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <issue.icon className="w-5 h-5 text-accent" />
                  </div>
                  <h3 className="font-bold mb-2">{issue.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{issue.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ SERVICES OFFERED ═══════════ */}
      <section className="container py-16 md:py-20 max-w-4xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold mb-8 font-display">
            Services We Offer in {data.town}
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {servicesOffered.map(svc => (
              <motion.div key={svc.label} variants={fadeUp}>
                <Link
                  to={svc.href}
                  className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-accent/40 hover:bg-accent/5 transition-all group"
                >
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="font-medium text-sm group-hover:text-accent transition-colors">{svc.label}</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ═══════════ CASE STUDY (if exists) ═══════════ */}
      {data.caseStudy && (
        <section className="bg-primary py-16 md:py-20">
          <div className="container max-w-4xl">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4">Case Study</motion.p>
              <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold mb-6 font-display text-primary-foreground">
                {data.caseStudy.title}
              </motion.h2>
              <motion.p variants={fadeUp} className="text-primary-foreground/80 leading-relaxed text-pretty text-lg">
                {data.caseStudy.story}
              </motion.p>
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══════════ TRUST SECTION ═══════════ */}
      <section className="container py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold mb-8 font-display">
              Why {data.town} Homeowners Choose Semper Fi
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: Shield, title: "Veteran-Owned & Operated", desc: "USMC values of integrity, discipline, and accountability in every service call." },
                { icon: Clock, title: "33 Years Pocono Experience", desc: "We've been repairing wells across the Pocono Mountains since 1992 — long before most competitors arrived." },
                { icon: MapPin, title: "Born & Raised Local", desc: "We know the terrain, the geology, the water conditions, and the wells — because we live here." },
                { icon: Wrench, title: "Same-Day Emergency Response", desc: "When you have no water, we prioritize your call and respond the same day throughout our service area." },
              ].map(item => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-5 rounded-xl bg-secondary/50 border border-border">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-accent/10">
                    <item.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-1">{item.title}</h3>
                    <p className="text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ EMERGENCY CTA ═══════════ */}
      <section className="bg-accent py-16 md:py-20">
        <div className="container text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-extrabold text-white mb-4 font-display">
              Lost Water in {data.town}? Call Now.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/80 mb-8 max-w-lg mx-auto">
              We respond same-day to emergency pump failures. Don't wait — call now and we'll get your water back fast.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild size="xl" className="bg-white text-accent hover:bg-white/90 font-bold shadow-lg">
                <a href={PHONE_HREF}>
                  <Phone className="w-5 h-5" />
                  Call {PHONE_NUMBER}
                </a>
              </Button>
              <Button asChild size="xl" className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold">
                <Link to="/contact">
                  <FileText className="w-5 h-5" />
                  Request Free Quote
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ OTHER SERVICE AREAS ═══════════ */}
      <section className="container py-16 md:py-20">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-xl font-extrabold mb-6 font-display">
            Other Areas We Serve
          </motion.h2>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
            {otherTowns.map(town => (
              <Link
                key={town.slug}
                to={town.href}
                className="text-sm text-muted-foreground bg-secondary hover:bg-accent/10 hover:text-accent px-3 py-1.5 rounded-lg transition-colors"
              >
                {town.name}
              </Link>
            ))}
          </motion.div>
        </motion.div>
      </section>
    </Layout>
  );
};

export default GeoServicePage;
